// --- Package Imports ---
import { DateTime } from "luxon";

// --- Helpers for Formatting Functions ---

// Modified .isInteger() method with polyfill for IE
Number.isInteger =
  Number.isInteger ||
  function (value) {
    return (
      typeof value === "number" &&
      isFinite(value) &&
      Math.floor(value) === value
    );
  };

// --- Formatting Functions ---

export const formatPrice = (price: string): string => {
  const parsed = parseFloat(price);

  return parsed % 1 === 0 ? parsed.toFixed(2) : parseFloat(price).toString();
};

// Used for quantity fields in the table component
// styling is different for trailing zeroes therefore
// we use this to generate the characters in a nested <span>
export const generateTrailingZeroes = (quantity: string): string => {
  let length: number = 0;

  if (quantity === "0") return ".00000000";

  if (Number.isNaN(quantity)) {
    return "Invalid number";
  }

  if (Number.isInteger(parseFloat(quantity))) {
    return ".00000000";
  }

  length =
    (quantity && quantity.split(".")[1] && quantity.split(".")[1].length) || 0;

  if (length >= 8) return "";

  return new Array(9 - length).join("0");
};

export const formatCurrency = (str: string) => {
  // Remove the dollar sign (and any other non-numeric characters except the decimal point)
  let numberStr = str.replace(/[^0-9.]/g, "");

  // Parse the string to a float to handle any leading zeros
  let number = parseFloat(numberStr);

  // Check if it's a valid number
  if (isNaN(number)) {
    return "Invalid number";
  }

  // Format the number with commas and convert it back to a string
  let formattedNumber = number.toLocaleString();

  // Add the dollar sign back
  return "$" + formattedNumber;
};

export const formatIsoDateToLocale = (iso: string) => {
  return DateTime.fromISO(iso).toUTC().toFormat("LLL d, h:mm:ss a");
};

export const capitalizeFirstChars = (split_str: string[]): string => {
  return split_str
    .map((string) => {
      if (!string) return "";
      return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
    })
    .join(" ");
};

export const formatNumberWithCommas = (
  number: number,
  fixed: boolean,
  fixed_value?: number
): string => {
  if (fixed) {
    return new Intl.NumberFormat("en-US", {
      minimumFractionDigits: fixed_value || 8,
      maximumFractionDigits: fixed_value || 8,
    }).format(parseFloat(number.toString()));
  } else {
    return new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 8,
    }).format(number);
  }
};

export const trimStrings = (payload: any): any => {
  if (typeof payload === "string") {
    return payload.trim();
  } else if (Array.isArray(payload)) {
    return payload.map(trimStrings);
  } else if (typeof payload === "object" && payload !== null) {
    const trimmedPayload: any = {};
    for (const key in payload) {
      if (payload.hasOwnProperty(key)) {
        trimmedPayload[key] = trimStrings(payload[key]);
      }
    }
    return trimmedPayload;
  }
  return payload;
};
